import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useInbox, useMarkInboxRead, useDeleteInboxMessages } from '../hooks/useGame';
import { fetchCollabInbox, respondToCollabRequest } from '../services/api';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAppStore } from '../stores/appStore';
import type { CollabRequest } from '../types';
import './ProfileModal.css'; // Reusing styles
import './InboxModal.css';

interface InboxModalProps {
    isOpen: boolean;
    onClose: () => void;
}

interface InboxMessage {
    id: number;
    title: string;
    body: string;
    type: string;
    created_at: string;
    is_read?: boolean;
    read?: boolean;
}

export default function InboxModal({ isOpen, onClose }: InboxModalProps) {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const setSessionId = useAppStore(state => state.setSessionId);
    const setIsCollab = useAppStore(state => state.setIsCollab);
    const setCurrentPuzzleId = useAppStore(state => state.setCurrentPuzzleId);
    const setGameState = useAppStore(state => state.setGameState);
    const setCurrentMode = useAppStore(state => state.setCurrentMode);

    const { data, isLoading } = useInbox();
    const { mutate: markRead } = useMarkInboxRead();
    const { mutate: deleteMessages, isPending: isDeleting } = useDeleteInboxMessages();

    // Fetch collab requests (party invites)
    const { data: collabData, isLoading: isLoadingCollab } = useQuery({
        queryKey: ['collabInbox'],
        queryFn: fetchCollabInbox,
        enabled: isOpen,
        refetchInterval: 30000,
    });

    const [respondingId, setRespondingId] = useState<number | null>(null);
    const [statusMessage, setStatusMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
    const [selectedMessages, setSelectedMessages] = useState<Set<number>>(new Set());
    const [expandedMessage, setExpandedMessage] = useState<number | null>(null);

    const messages: InboxMessage[] = data?.messages || [];
    const partyInvites = collabData?.requests || [];

    // Reset selection when modal closes
    useEffect(() => {
        if (!isOpen) {
            setSelectedMessages(new Set());
            setExpandedMessage(null);
        }
    }, [isOpen]);

    const handleToggleSelect = (id: number, e: React.MouseEvent) => {
        e.stopPropagation();
        setSelectedMessages(prev => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    const handleSelectAll = () => {
        if (selectedMessages.size === messages.length) {
            setSelectedMessages(new Set());
        } else {
            setSelectedMessages(new Set(messages.map(m => m.id)));
        }
    };

    const handleDeleteSelected = () => {
        if (selectedMessages.size === 0) return;
        deleteMessages(Array.from(selectedMessages), {
            onSuccess: () => {
                setSelectedMessages(new Set());
                setStatusMessage({ text: `${selectedMessages.size} message(s) deleted`, type: 'success' });
            },
            onError: () => {
                setStatusMessage({ text: 'Failed to delete messages', type: 'error' });
            }
        });
    };

    const handleDeleteSingle = (id: number, e: React.MouseEvent) => {
        e.stopPropagation();
        deleteMessages([id], {
            onSuccess: () => {
                if (expandedMessage === id) {
                    setExpandedMessage(null);
                }
            }
        });
    };

    const handleMarkAllRead = () => {
        const unreadIds = messages.filter(m => !(m.read || m.is_read)).map(m => m.id);
        if (unreadIds.length > 0) {
            markRead(unreadIds);
        }
    };

    const handleExpandMessage = (msg: InboxMessage) => {
        // Toggle expand
        if (expandedMessage === msg.id) {
            setExpandedMessage(null);
        } else {
            setExpandedMessage(msg.id);
            // Mark as read when expanded
            if (!(msg.read || msg.is_read)) {
                markRead([msg.id]);
            }
        }
    };

    const handleRespondToInvite = useCallback(async (requestId: number, accept: boolean) => {
        setRespondingId(requestId);
        setStatusMessage(null);

        try {
            const result = await respondToCollabRequest(requestId, accept) as {
                success: boolean;
                message?: string;
                session_id?: string;
                puzzle_id?: string;
            };

            if (result.success) {
                // Refresh the collab inbox
                queryClient.invalidateQueries({ queryKey: ['collabInbox'] });

                if (accept && result.session_id && result.puzzle_id) {
                    console.log("[InboxModal] Accepting invite, setting puzzle to:", result.puzzle_id);
                    const mode = result.puzzle_id.startsWith("flip_") ? "flip" : result.puzzle_id.startsWith("bridge_") ? "bridge" : "classic";
                    console.log("[InboxModal] Setting mode to:", mode);

                    // IMPORTANT: set collab state BEFORE changing mode.
                    // setCurrentMode triggers AppContent's fetchPuzzles effect, which guards on
                    // isCollab to avoid overriding the collab puzzle ID. If isCollab isn't set
                    // first, the guard misses and fetchPuzzles resets puzzleId to the default.
                    setIsCollab(true);
                    setSessionId(result.session_id);
                    setCurrentPuzzleId(result.puzzle_id);
                    setGameState(null); // Clear stale game state to force a fresh fetch

                    // Now change mode — this triggers the AppContent effect, which will see
                    // isCollab=true and skip the puzzle-ID override.
                    setCurrentMode(mode);

                    // Invalidate the game query for the specific puzzle to force a fresh fetch
                    await queryClient.invalidateQueries({ queryKey: ['game', result.puzzle_id] });
                    await queryClient.invalidateQueries({ queryKey: ['game'] });

                    setStatusMessage({ text: 'Joined the party! 🎉', type: 'success' });

                    setTimeout(() => {
                        navigate('/play');
                        onClose();
                    }, 500);
                } else if (accept) {
                    setStatusMessage({ text: 'Joined the party! 🎉', type: 'success' });
                    setTimeout(() => onClose(), 1500);
                } else {
                    setStatusMessage({ text: 'Invite declined', type: 'success' });
                }
            } else {
                setStatusMessage({ text: result.message || 'Failed to respond', type: 'error' });
            }
        } catch (error) {
            setStatusMessage({ text: 'Failed to respond to invite', type: 'error' });
        } finally {
            setRespondingId(null);
        }
    }, [queryClient, setSessionId, setIsCollab, setCurrentPuzzleId, onClose, navigate]);

// Auto-clear status message
useEffect(() => {
    if (statusMessage) {
        const timeout = setTimeout(() => setStatusMessage(null), 3000);
        return () => clearTimeout(timeout);
    }
}, [statusMessage]);

const hasPartyInvites = partyInvites.length > 0;
const hasMessages = messages.length > 0;
const isAnyLoading = isLoading || isLoadingCollab;
const unreadCount = messages.filter(m => !(m.read || m.is_read)).length;

// Helper to format date nicely
const formatDate = (dateStr: string | number) => {
    const date = new Date(typeof dateStr === 'number' ? dateStr * 1000 : dateStr);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
        return 'Yesterday';
    } else if (diffDays < 7) {
        return date.toLocaleDateString([], { weekday: 'short' });
    }
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

const isRead = (msg: InboxMessage) => msg.read || msg.is_read;

return (
    <AnimatePresence>
        {isOpen && (
            <div className="profile-modal-overlay" onClick={onClose}>
                <motion.div
                    className="profile-modal inbox-modal"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    onClick={e => e.stopPropagation()}
                >
                    <div className="profile-modal-header">
                        <h2 className="profile-modal-title">
                            📥 Inbox
                            {unreadCount > 0 && (
                                <span className="inbox-unread-badge">{unreadCount}</span>
                            )}
                        </h2>
                        <button className="profile-modal-close" onClick={onClose}>×</button>
                    </div>

                    {/* Toolbar */}
                    {hasMessages && (
                        <div className="inbox-toolbar">
                            <div className="inbox-toolbar-left">
                                <label className="inbox-checkbox-wrapper">
                                    <input
                                        type="checkbox"
                                        checked={selectedMessages.size === messages.length && messages.length > 0}
                                        onChange={handleSelectAll}
                                    />
                                    <span className="inbox-checkbox-label">Select all</span>
                                </label>
                                {selectedMessages.size > 0 && (
                                    <button
                                        className="inbox-toolbar-btn delete"
                                        onClick={handleDeleteSelected}
                                        disabled={isDeleting}
                                    >
                                        🗑️ Delete ({selectedMessages.size})
                                    </button>
                                )}
                            </div>
                            <div className="inbox-toolbar-right">
                                {unreadCount > 0 && (
                                    <button className="inbox-toolbar-btn" onClick={handleMarkAllRead}>
                                        ✓ Mark all read
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Status Message */}
                    <AnimatePresence>
                        {statusMessage && (
                            <motion.div
                                className={`inbox-status ${statusMessage.type}`}
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                            >
                                {statusMessage.text}
                            </motion.div>
                        )}
                    </AnimatePresence>

                    <div className="inbox-content">
                        {isAnyLoading ? (
                            <div className="loading-spinner">Loading...</div>
                        ) : !hasPartyInvites && !hasMessages ? (
                            <div className="empty-state">
                                <span className="empty-icon">📭</span>
                                <p>Your inbox is empty</p>
                                <span className="empty-hint">Party invites and notifications will appear here</span>
                            </div>
                        ) : (
                            <>
                                {/* Party Invites Section */}
                                {hasPartyInvites && (
                                    <div className="inbox-section">
                                        <h3 className="inbox-section-title">
                                            <span className="section-icon">🎉</span>
                                            Party Invites
                                        </h3>
                                        <div className="invite-list">
                                            {partyInvites.map((invite: CollabRequest) => (
                                                <div key={invite.id} className="invite-item">
                                                    <div className="invite-info">
                                                        <span className="invite-sender">{invite.sender_name}</span>
                                                        <span className="invite-puzzle">
                                                            wants to play <strong>{invite.puzzle_id}</strong>
                                                        </span>
                                                    </div>
                                                    <div className="invite-actions">
                                                        <button
                                                            className="invite-accept-btn"
                                                            onClick={() => handleRespondToInvite(invite.id, true)}
                                                            disabled={respondingId === invite.id}
                                                        >
                                                            {respondingId === invite.id ? '...' : 'Join'}
                                                        </button>
                                                        <button
                                                            className="invite-decline-btn"
                                                            onClick={() => handleRespondToInvite(invite.id, false)}
                                                            disabled={respondingId === invite.id}
                                                        >
                                                            ✕
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Messages Section - Email Style */}
                                {hasMessages && (
                                    <div className="inbox-section">
                                        {hasPartyInvites && (
                                            <h3 className="inbox-section-title">
                                                <span className="section-icon">💬</span>
                                                Notifications
                                            </h3>
                                        )}
                                        <div className="email-list">
                                            {messages.map(msg => (
                                                <div
                                                    key={msg.id}
                                                    className={`email-item ${isRead(msg) ? 'read' : 'unread'} ${expandedMessage === msg.id ? 'expanded' : ''} ${selectedMessages.has(msg.id) ? 'selected' : ''}`}
                                                >
                                                    <div
                                                        className="email-row"
                                                        onClick={() => handleExpandMessage(msg)}
                                                    >
                                                        <div className="email-checkbox" onClick={e => handleToggleSelect(msg.id, e)}>
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedMessages.has(msg.id)}
                                                                onChange={() => { }}
                                                            />
                                                        </div>
                                                        <div className={`email-indicator ${isRead(msg) ? '' : 'unread'}`} />
                                                        <div className="email-subject">
                                                            {msg.title}
                                                        </div>
                                                        <div className="email-preview">
                                                            {msg.body.substring(0, 50)}{msg.body.length > 50 ? '...' : ''}
                                                        </div>
                                                        <div className="email-date">
                                                            {formatDate(msg.created_at)}
                                                        </div>
                                                        <button
                                                            className="email-delete-btn"
                                                            onClick={e => handleDeleteSingle(msg.id, e)}
                                                            title="Delete"
                                                        >
                                                            🗑️
                                                        </button>
                                                    </div>

                                                    {/* Expanded content */}
                                                    <AnimatePresence>
                                                        {expandedMessage === msg.id && (
                                                            <motion.div
                                                                className="email-body"
                                                                initial={{ opacity: 0, height: 0 }}
                                                                animate={{ opacity: 1, height: 'auto' }}
                                                                exit={{ opacity: 0, height: 0 }}
                                                            >
                                                                <p>{msg.body}</p>
                                                            </motion.div>
                                                        )}
                                                    </AnimatePresence>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </motion.div>
            </div>
        )}
    </AnimatePresence>
);
}
